import React, { useEffect, useState, ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getPlatformInfo } from '../../utils/platform';
import { postMessageService } from '../../services/postMessage';
import { CommunicationBridge } from '@sequelae/lore-mfe-kit';
import { Button, Typography } from '@sequelae/lore-core';
import { useNavigationMode } from '../../contexts/NavigationModeContext';
import { NavigationModeToggle } from '../../components/NavigationModeToggle';
import { NavigationTestButtons } from '../../components/NavigationTestButtons';
import '../../styles/menu.css';
import '../../styles/shared-styles.css';
import '../../styles/ios-styles.css';

// Define menu item interface
interface MenuItem {
  title: string;
  path: string;
  icon: ReactNode;
}

// Menu items configuration
const menuItems: MenuItem[] = [
  {
    title: 'Account information',
    path: '/account-ios',
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 6C13.1 6 14 6.9 14 8C14 9.1 13.1 10 12 10C10.9 10 10 9.1 10 8C10 6.9 10.9 6 12 6ZM12 13C9.33 13 4 14.34 4 17V19C4 19.55 4.45 20 5 20H19C19.55 20 20 19.55 20 19V17C20 14.34 14.67 13 12 13ZM18 18H6V17.01C6.2 16.29 9.3 15 12 15C14.7 15 17.8 16.29 18 17V18Z"
          fill="#007D8A"
        />
      </svg>
    ),
  },
  {
    title: 'About',
    path: '/about-ios',
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20ZM11 11H13V17H11V11ZM11 7H13V9H11V7Z"
          fill="#007D8A"
        />
      </svg>
    ),
  },
];

export const MenuIOS = () => {
  const [showDetails, setShowDetails] = useState(false);
  const [isForced, setIsForced] = useState(() => localStorage.getItem('forcedStyle') === 'android');
  const navigate = useNavigate();
  const platformInfo = getPlatformInfo();
  const { isNativeMode } = useNavigationMode();

  const handleNativePush = (path: string) => {
    console.log(`🚀 CommunicationBridge.openMFEModule: MenuMFE → ${path}`);
    // CommunicationBridge handles all fallbacks internally (postMessage, routing, etc.)
    CommunicationBridge.openMFEModule({
      url: 'https://menu-mfe-nonprod.lorehealthcare.io',
      scope: 'MenuMFE',
      module: 'MenuMFE',
      path: path,
    });
  };

  const handleNavigation = (path: string) => {
    if (isNativeMode) {
      handleNativePush(path);
    } else {
      navigate(path);
    }
  };

  const handleForceToggle = (checked: boolean) => {
    console.log('[MenuMFE iOS] Force toggle clicked, checked:', checked);

    if (checked) {
      // Force Android style
      localStorage.setItem('forcedStyle', 'android');
      console.log('[MenuMFE iOS] Navigating to Android menu');
      navigate('/menu');
    } else {
      // Remove forced style (stay on iOS)
      localStorage.removeItem('forcedStyle');
      console.log('[MenuMFE iOS] Removed forced style');
    }
    setIsForced(checked);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const detailsPanel = document.querySelector('.details-panel');
      const detailsButton = document.querySelector('.details-button');
      if (
        showDetails &&
        detailsPanel &&
        detailsButton &&
        !detailsPanel.contains(event.target as Node) &&
        !detailsButton.contains(event.target as Node)
      ) {
        setShowDetails(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDetails]);

  const handleItemPress = () => {
    if ('vibrate' in navigator) {
      navigator.vibrate(10);
    }
  };

  return (
    <div className="screen ios">
      <div className="shared-container">
        <div className="menu-container ios">
          <header className="menu-header mb-4">
            <Typography variant="h1" className="ios-large-title">
              Menu
            </Typography>
            <NavigationModeToggle />
          </header>
          <div
            className="ios-card"
            style={{
              margin: '0 var(--spacing-space-4) var(--spacing-space-4)',
            }}
          >
            {menuItems.map((item, index) =>
              isNativeMode ? (
                <Button
                  key={index}
                  onClick={() => {
                    handleItemPress();
                    handleNavigation(item.path);
                  }}
                  className="ios-list-item"
                  style={{
                    borderBottom:
                      index === menuItems.length - 1 ? 'none' : '0.5px solid rgba(0, 0, 0, 0.15)',
                  }}
                >
                  <div className="flex-row gap-3 flex-1">
                    <div className="flex-shrink-0">{item.icon}</div>
                    <Typography variant="body" className="ios-list-item-text">
                      {item.title}
                    </Typography>
                  </div>
                  <svg
                    width="8"
                    height="13"
                    viewBox="0 0 8 13"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="flex-shrink-0"
                  >
                    <path d="M0.5 1L6.5 6.5L0.5 12" stroke="#C7C7CC" strokeWidth="1.5" />
                  </svg>
                </Button>
              ) : (
                <Link
                  key={index}
                  to={item.path}
                  onClick={handleItemPress}
                  className="ios-list-item"
                  style={{
                    borderBottom:
                      index === menuItems.length - 1 ? 'none' : '0.5px solid rgba(0, 0, 0, 0.15)',
                  }}
                >
                  <div className="flex-row gap-3 flex-1">
                    <div className="flex-shrink-0">{item.icon}</div>
                    <Typography variant="body" className="ios-list-item-text">
                      {item.title}
                    </Typography>
                  </div>
                  <svg
                    width="8"
                    height="13"
                    viewBox="0 0 8 13"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="flex-shrink-0"
                  >
                    <path d="M0.5 1L6.5 6.5L0.5 12" stroke="#C7C7CC" strokeWidth="1.5" />
                  </svg>
                </Link>
              )
            )}
          </div>
          <div className="menu-details-wrapper">
            <Button
              onClick={() => setShowDetails(!showDetails)}
              className="details-button ios"
              variant="secondary"
            >
              More details
            </Button>
            {showDetails && (
              <div className="details-panel ios">
                <div className="details-header">
                  <Typography variant="body">Current Style: iOS</Typography>
                </div>
                <div className="details-content">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      onChange={e => handleForceToggle(e.target.checked)}
                      checked={isForced}
                    />
                    <Typography variant="body">Force Android Style</Typography>
                  </label>
                  <div
                    className="mt-3 text-secondary"
                    style={{
                      fontSize: 'var(--typography-font-sizes-xs)',
                    }}
                  >
                    <Typography variant="caption" color="secondary">
                      Detected:{' '}
                      {platformInfo.isIOS
                        ? 'iOS'
                        : platformInfo.platform === 'android'
                        ? 'Android'
                        : 'Web'}
                    </Typography>
                    <Typography variant="caption" color="secondary" className="mt-1">
                      Window: {window.innerWidth}x{window.innerHeight}
                    </Typography>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="navigation-test-spacer">
            <NavigationTestButtons />
          </div>
        </div>
      </div>
    </div>
  );
};
